import React, { useContext, useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import moment from 'moment';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
// Context
import useProductSamplingContext from '../context/useProductSamplingContext';
// Helpers
import { filterValue, filterRepOptions } from '../../../helpers/filterFunctions';
// API
import { getRepFilter } from '../../../api/filterOptionsApi';
import { postProductSamplingPpsrs } from '../../../api/productSamplingDashboard';

export default function PpsrsModal() {
  const { filterForm, ppsvsrsModal, setPpsvsrsModal } = useContext(useProductSamplingContext);

  const modalFilterFormDefault = {
    team: filterForm?.team?.value,
    rep_id: { label: 'ALL', value: null },
    date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
    date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
  };

  const [loading, setLoading] = useState(false);
  const [modalFilterForm, setModalFilterForm] = useState(modalFilterFormDefault);
  const [repOptions, setRepOptions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [sortModel, setSortModel] = useState([
    {
      field: 'id',
      sort: 'desc',
    },
  ]);

  const columns = [
    {
      field: 'head_name',
      headerName: 'Regional Manager',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => row.head_name,
    },
    {
      field: 'manager_name',
      headerName: 'District Manager',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => row.manager_name,
    },
    {
      field: 'rep_name',
      headerName: 'Rep Name',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => row.rep_name,
    },
    {
      field: 'product_name',
      headerName: 'Product Sample',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'allocation',
      headerName: 'Product Allocation',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) =>
        Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row.allocation),
    },
    {
      field: 'dispense',
      headerName: 'Product Distributed',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) =>
        Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row.dispense),
    },
    {
      field: 'perf',
      headerName: 'Perf %',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => `${row.perf}%`,
    },
  ];

  const handleCancel = () => {
    setPpsvsrsModal(false);
  };

  const loadRepDropdown = async () => {
    const request = {
      team: filterValue(filterForm?.team),
      date_from: filterForm?.date_from,
      date_to: filterForm?.date_from,
    };
    const reponse = await getRepFilter(request);
    const { code, data } = reponse.data;
    if (code === '200') {
      const dropdownValue = filterRepOptions(data);
      setRepOptions([{ label: 'ALL', value: null }, ...dropdownValue]);
    }
  };

  const handleGenerateFilter = async (clear = null) => {
    try {
      setLoading(true);
      let request;
      if (clear === 'reset') {
        request = {
          team: modalFilterFormDefault?.team,
          rep_id: modalFilterFormDefault?.rep_id?.value,
          date_from: moment(modalFilterFormDefault?.date_from).format('YYYY-MM-DD'),
          date_to: moment(modalFilterFormDefault?.date_to).format('YYYY-MM-DD'),
          type: 'list',
        };
      } else {
        request = {
          team: modalFilterForm?.team,
          rep_id: modalFilterForm?.rep_id?.value,
          date_from: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(modalFilterForm?.date_to).format('YYYY-MM-DD'),
          type: 'list',
        };
      }

      const response = await postProductSamplingPpsrs(request);
      const { code, data } = response.data;

      if (code === '200') {
        setTableData(data);
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeFilter = (e, row, name) => {
    setModalFilterForm({
      ...modalFilterForm,
      [name]: row,
    });
  };

  const handleClearFilter = () => {
    setModalFilterForm(modalFilterFormDefault);
    handleGenerateFilter('reset');
  };

  const handleExportBreakdown = async () => {
    try {
      toast.loading('Please wait ...');

      const request = {
        team: modalFilterForm?.team,
        rep_id: modalFilterForm?.rep_id?.value,
        date_from: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
        date_to: moment(modalFilterForm?.date_to).format('YYYY-MM-DD'),
        type: 'report',
      };

      const res = await postProductSamplingPpsrs(request);

      const { code, result } = res.data.response;
      if (code === '200') {
        window.location.href = result;
        toast.success(`Successfully downloaded file: ${res?.data?.response?.file_name}`);
      }
      toast.dismiss();
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const pinnedRows = {
    top: tableData?.length ? [tableData[0]] : [],
  };

  useEffect(() => {
    loadRepDropdown();
  }, [modalFilterForm]);

  useEffect(() => {
    handleGenerateFilter();
  }, []);

  return (
    <div>
      <Dialog open={ppsvsrsModal} onClose={handleCancel} maxWidth='auto' fullWidth>
        <DialogTitle>
          Per Product Samples vs Remaining Samples
          <IconButton
            aria-label='close'
            onClick={handleCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pl={2} pt={2}>
            <Grid item xs={12} sm={12} md={2} lg={2}>
              <Autocomplete
                disablePortal
                id='rep_id'
                disableClearable
                fullWidth
                options={repOptions}
                // eslint-disable-next-line react/jsx-props-no-spreading
                renderInput={(params) => <TextField {...params} label='MPR' />}
                size='small'
                value={modalFilterForm?.rep_id}
                onChange={(e, newValue) => handleChangeFilter(e, newValue, 'rep_id')}
                sx={{ backgroundColor: '#fff' }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2} lg={2}>
              <Button variant='contained' color='success' onClick={handleGenerateFilter}>
                Generate
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={() => handleClearFilter()}
                sx={{ marginLeft: '10px' }}
              >
                Clear
              </Button>
            </Grid>
            <Grid item xs={12} sm={12} md={8} lg={8}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant='contained'
                  color='warning'
                  onClick={() => handleExportBreakdown()}
                  startIcon={<CloudDownloadIcon />}
                  sx={{
                    marginLeft: '10px',
                    paddingTop: '10px',
                  }}
                >
                  Export
                </Button>
              </Box>
            </Grid>
          </Grid>
          {/* <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={6} lg={6} pl={3} pt={3}>
             
            </Grid>
          </Grid> */}
          <Grid container spacing={2} pl={2} pt={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Box
                className='table-container'
                sx={{
                  height: `600px`,
                  '& .super-app-theme--header': {
                    fontWeight: 'bold',
                    backgroundColor: '#808080',
                    color: '#fff',
                  },
                  '& .MuiDataGrid-pinnedRows': { fontWeight: 'bold', backgroundColor: '#FBCEB1' },
                }}
              >
                <DataGridPro
                  loading={loading}
                  rows={tableData}
                  columns={columns}
                  sortModel={sortModel}
                  experimentalFeatures={{ rowPinning: true }}
                  pinnedRows={pinnedRows}
                  initialState={{
                    ...tableData.initialState,
                    pagination: {
                      pageSize: 50,
                    },
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  pagination
                  onSortModelChange={(model) => setSortModel(model)}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' color='error' onClick={handleCancel}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
